import { useEffect, useState, useCallback, useRef } from "react";
import DocumentExtractions from './DocumentExtractions';
import api, { uploadEvidenceChunked, downloadFile, humanSize, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Paperclip, Upload, Download, AlertTriangle, FileArchive, Check, RefreshCw, Ban, Copy, ChevronDown, ChevronRight } from "lucide-react";

const STATUS_STYLE = {
  "Memadai": "bg-green-100 text-green-700 border-green-300",
  "Tidak Memadai": "bg-red-100 text-red-700 border-red-300",
  "Dalam Reviu": "bg-amber-100 text-amber-800 border-amber-300",
  "Bukti Diunggah": "bg-blue-100 text-blue-700 border-blue-300",
  "Digantikan": "bg-slate-100 text-slate-500 border-slate-300",
};

let META_CACHE = null;
async function loadMeta() {
  if (META_CACHE) return META_CACHE;
  const { data } = await api.get("/meta/evidence");
  META_CACHE = data;
  return data;
}

// ---------- UPLOAD DIALOG ----------
export function UploadDialog({ open, onOpenChange, eid, defaults, lockContext, onDone, replaceId }) {
  const [meta, setMeta] = useState(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => { loadMeta().then(setMeta); }, []);
  useEffect(() => {
    if (open) { setForm({ confidentiality: "Normal", is_reference: false, ...defaults }); setFile(null); setProgress(0); }
  }, [open, defaults]);
  if (!meta) return null;

  const submit = async () => {
    if (!file) return toast.error("Pilih berkas terlebih dahulu");
    if (!form.category && !replaceId) return toast.error("Pilih kategori dokumen");
    setBusy(true);
    try {
      await uploadEvidenceChunked(eid, file, form, {
        replaceId, replacementReason: form.replacement_reason, onProgress: setProgress,
      });
      toast.success("Bukti terunggah");
      onOpenChange(false); onDone && onDone();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="evidence-upload-dialog">
        <DialogHeader><DialogTitle>{replaceId ? "Unggah Versi Baru" : "Tambah Bukti / Dokumen Pendukung"}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
          <input type="file" onChange={(e) => setFile(e.target.files[0])} className="text-xs" data-testid="evidence-file-input"
            accept=".xlsx,.xls,.csv,.docx,.doc,.pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.zip" />
          {file && <div className="text-[11px] text-muted-foreground">{file.name} — {humanSize(file.size)}</div>}
          {lockContext ? (
            <div className="text-[11px] text-muted-foreground">Fase: <b>{form.phase}</b> · Modul: <b>{form.module}</b></div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.phase || ""} onValueChange={(v) => setForm({ ...form, phase: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="evidence-phase"><SelectValue placeholder="Fase" /></SelectTrigger>
                <SelectContent>{meta.phases.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="h-8 text-xs" placeholder="Modul" value={form.module || ""} onChange={(e) => setForm({ ...form, module: e.target.value })} />
            </div>
          )}
          {!replaceId && (
            <Select value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="evidence-category"><SelectValue placeholder="Kategori dokumen" /></SelectTrigger>
              <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input className="h-8 text-xs" placeholder="Ref KKA" value={form.wp_ref || ""} onChange={(e) => setForm({ ...form, wp_ref: e.target.value })} />
            <Input className="h-8 text-xs" placeholder="Area Audit" value={form.audit_area || ""} onChange={(e) => setForm({ ...form, audit_area: e.target.value })} />
            <Input className="h-8 text-xs" placeholder="Sumber" value={form.source || ""} onChange={(e) => setForm({ ...form, source: e.target.value })} />
            <Input className="h-8 text-xs" placeholder="Pihak Sumber" value={form.source_party || ""} onChange={(e) => setForm({ ...form, source_party: e.target.value })} />
            <Input className="h-8 text-xs" type="date" value={form.document_date || ""} onChange={(e) => setForm({ ...form, document_date: e.target.value })} title="Tanggal Dokumen" />
            <Select value={form.confidentiality || "Normal"} onValueChange={(v) => setForm({ ...form, confidentiality: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.confidentiality.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="block text-xs">ID entitas pada sumber (opsional)<Input aria-label="ID entitas pada sumber" maxLength={200} value={form.entity_id || ""} onChange={e=>setForm({...form,entity_id:e.target.value})}/><span>Gunakan ID yang tercantum pada sumber; kosongkan jika belum diketahui.</span></label>
          <Textarea rows={2} className="text-xs" placeholder="Deskripsi / relevansi bukti" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          {replaceId && <Input className="h-8 text-xs" placeholder="Alasan penggantian (wajib untuk versi baru)" value={form.replacement_reason || ""} onChange={(e) => setForm({ ...form, replacement_reason: e.target.value })} data-testid="evidence-replace-reason" />}
          {!replaceId && (
            <label className="flex items-center gap-2 text-[11px]">
              <Checkbox checked={!!form.is_reference} onCheckedChange={(v) => setForm({ ...form, is_reference: !!v })} data-testid="evidence-is-reference" />
              Dokumen Contoh / Referensi (REFERENSI SAJA — BUKAN BUKTI AUDIT TAHUN BERJALAN)
            </label>
          )}
          {busy && <Progress value={progress} className="h-2" />}
        </div>
        <DialogFooter>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="evidence-upload-submit">
            {busy ? `Mengunggah ${progress}%` : "Unggah"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- ZIP CONTENTS ----------
function ZipContents({ eid, zipId, summary, reload }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const load = () => api.get(`/engagements/${eid}/evidence?parent_zip_id=${zipId}&include_extracted=1`).then((r) => setRows(r.data));
  useEffect(() => { if (open) { load(); loadMeta().then(setMeta); } }, [open]); // eslint-disable-line
  const classify = async (id, category) => { await api.put(`/evidence/${id}`, { category }); load(); reload && reload(); };
  return (
    <div className="mt-1">
      <button className="text-[11px] text-indigo-600 flex items-center gap-1" onClick={() => setOpen(!open)} data-testid={`zip-toggle-${zipId}`}>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Isi ZIP ({summary?.extracted || 0} diekstrak, {summary?.rejected || 0} ditolak)
      </button>
      {open && (
        <div className="mt-1 border border-border rounded-sm p-2 bg-muted/30 space-y-1">
          {rows.map((x) => (
            <div key={x.id} className="flex items-center gap-2 text-[11px]" data-testid={`zip-item-${x.id}`}>
              <span className="flex-1 truncate">{x.display_name || x.filename}</span>
              {x.needs_classification && <span className="text-red-600">perlu klasifikasi</span>}
              {meta && (
                <Select value={x.category || ""} onValueChange={(v) => classify(x.id, v)}>
                  <SelectTrigger className="h-6 text-[10px] w-40"><SelectValue placeholder="Klasifikasi…" /></SelectTrigger>
                  <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => downloadFile(`/evidence/${x.id}/download`, x.display_name || x.filename)}><Download size={12} /></Button>
            </div>
          ))}
          {(summary?.exceptions || []).length > 0 && (
            <div className="pt-1 border-t border-border/60">
              <div className="text-[10px] font-semibold text-red-600 mb-0.5">Daftar Pengecualian (tidak diekstrak):</div>
              {summary.exceptions.map((e, i) => (
                <div key={i} className="text-[10px] text-muted-foreground" data-testid={`zip-exc-${i}`}>{e.name} — {e.reason}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- EVIDENCE ROW ACTIONS ----------
function useReview(reload) {
  return useCallback(async (ev, action, extra = {}) => {
    try {
      const note = ["request_more", "irrelevant", "link_review_note", "link_exception", "link_aje", "link_fraud"].includes(action)
        ? window.prompt("Catatan / referensi:") || "" : "";
      await api.post(`/evidence/${ev.id}/review`, { action, note, ...extra });
      toast.success("Tersimpan"); reload();
    } catch (e) { toast.error(getErrorMessage(e)); }
  }, [reload]);
}

function EvidenceTable({ eid, rows, reload, onReplace }) {
  const review = useReview(reload);
  const copyRef = async (ev) => { await api.post(`/evidence/${ev.id}/copy-to-current-year`, {}); toast.success("Disalin ke tahun berjalan"); reload(); };
  if (rows.length === 0) return <div className="text-xs text-muted-foreground py-3 text-center">Belum ada bukti terlampir.</div>;
  return (
    <div className="space-y-1">
      {rows.map((ev) => (
        <div key={ev.id} className="border border-border rounded-sm p-2 bg-white" data-testid={`evidence-row-${ev.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            {ev.is_zip ? <FileArchive size={14} className="text-amber-600" /> : <Paperclip size={14} className="text-muted-foreground" />}
            <span className="text-xs font-medium">{ev.display_name || ev.filename}</span>
            <span className="text-[10px] text-muted-foreground">v{ev.version} · {humanSize(ev.file_size)} · {ev.category || "—"}</span>
            <span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${STATUS_STYLE[ev.status] || "bg-muted"}`}>{ev.review_status || ev.status}</span>
            {ev.is_reference && <span className="text-[9px] px-1 py-0.5 border rounded-sm bg-violet-100 text-violet-700 border-violet-300" data-testid={`ref-label-${ev.id}`}>REFERENSI SAJA — BUKAN BUKTI TB</span>}
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="ghost" className="h-6 px-1" title="Unduh" onClick={() => downloadFile(`/evidence/${ev.id}/download`, ev.display_name || ev.original_filename)} data-testid={`ev-download-${ev.id}`}><Download size={13} /></Button>
              {ev.is_reference ? (
                <Button size="sm" variant="ghost" className="h-6 px-1 text-[11px]" title="Salin ke tahun berjalan" onClick={() => copyRef(ev)} data-testid={`ev-copy-${ev.id}`}><Copy size={13} /></Button>
              ) : ev.status !== "Digantikan" && (
                <>
                  <Button size="sm" variant="ghost" className="h-6 px-1 text-green-600" title="Terima" onClick={() => review(ev, "accept")} data-testid={`ev-accept-${ev.id}`}><Check size={13} /></Button>
                  <Button size="sm" variant="ghost" className="h-6 px-1 text-amber-600" title="Minta tambahan" onClick={() => review(ev, "request_more")} data-testid={`ev-request-${ev.id}`}><AlertTriangle size={13} /></Button>
                  <Button size="sm" variant="ghost" className="h-6 px-1 text-red-500" title="Tidak relevan" onClick={() => review(ev, "irrelevant")} data-testid={`ev-irrelevant-${ev.id}`}><Ban size={13} /></Button>
                  <Button size="sm" variant="ghost" className="h-6 px-1" title="Versi baru" onClick={() => onReplace(ev)} data-testid={`ev-version-${ev.id}`}><RefreshCw size={13} /></Button>
                </>
              )}
            </div>
          </div>
          {ev.entity_id && <p className="text-xs break-words">ID entitas sumber: {ev.entity_id}</p>}
          {ev.description && <div className="text-[10px] text-muted-foreground mt-0.5">{ev.description}</div>}
          {ev.reviewer && <div className="text-[10px] text-muted-foreground">Reviewer: {ev.reviewer} {ev.review_note && `· ${ev.review_note}`}</div>}
          {ev.is_zip && ev.zip_summary && <ZipContents eid={eid} zipId={ev.id} summary={ev.zip_summary} reload={reload} />}
        </div>
      ))}
    </div>
  );
}

// ---------- CONTEXTUAL PANEL ----------
export default function EvidencePanel({ id, phase, module, wpRef, auditArea, riskId, procedureId, sampleItem, sampleId, confirmationId, title }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [replaceEv, setReplaceEv] = useState(null);

  const ctx = { phase, module, wp_ref: wpRef, audit_area: auditArea, risk_id: riskId, procedure_id: procedureId, sample_item: sampleItem, sample_id: sampleId, confirmation_id: confirmationId };
  const linkedSource = wpRef || riskId || procedureId || sampleId || confirmationId;
  const qs = new URLSearchParams(Object.entries({ module: linkedSource ? undefined : module, wp_ref: wpRef, audit_area: auditArea, risk_id: riskId, procedure_id: procedureId, sample_id: sampleId, confirmation_id: confirmationId }).filter(([, v]) => v)).toString();

  const reload = useCallback(() => {
    api.get(`/engagements/${id}/evidence?${qs}`).then((r) => setRows(r.data));
    api.get(`/engagements/${id}/evidence/summary?${qs}`).then((r) => setSummary(r.data));
  }, [id, qs]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="mt-6 section-card overflow-hidden" data-testid={`evidence-panel-${module}`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-navy-50/60">
        <div className="flex items-center gap-2">
          <Paperclip size={15} className="text-navy-700" />
          <span className="font-head font-semibold text-[14px] text-navy-700">Bukti & Dokumen Pendukung{title ? ` — ${title}` : ""}</span>
        </div>
        <Button size="sm" onClick={() => { setReplaceEv(null); setDialog(true); }} data-testid={`evidence-add-${module}`}>
          <Upload size={13} className="mr-1" /> Tambah Bukti / Unggah Dokumen
        </Button>
      </div>
      {summary && (
        <div className="flex flex-wrap gap-2 px-4 py-2.5 text-[11px] border-b border-border/60">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-border rounded-md bg-white" data-testid="ev-stat-attached">Terlampir: <b className="text-navy-700">{summary.attached}</b></span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-border rounded-md bg-white">Bukti TB: <b className="text-navy-700">{summary.current_year}</b></span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-green-300 rounded-md bg-green-50 text-green-700">Direviu: <b>{summary.reviewed}</b></span>
          {summary.needs_classification > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 border rounded-md bg-amber-50 text-amber-800 border-amber-300"><AlertTriangle size={11} />Perlu Klasifikasi: <b>{summary.needs_classification}</b></span>}
          {summary.open_requests > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 border rounded-md bg-amber-50 text-amber-800 border-amber-300" data-testid="ev-stat-requests"><AlertTriangle size={11} />Permintaan Terbuka: <b>{summary.open_requests}</b></span>}
          {summary.current_year === 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 border rounded-md bg-red-50 text-red-700 border-red-300" data-testid="ev-missing-warning"><AlertTriangle size={11} /> Belum ada bukti tahun berjalan</span>}
        </div>
      )}
      <div className="px-4 py-3">
        <EvidenceTable eid={id} rows={rows} reload={reload} onReplace={(ev) => { setReplaceEv(ev); setDialog(true); }} />
      </div>
      <UploadDialog open={dialog} onOpenChange={setDialog} eid={id}
        defaults={replaceEv ? { phase, module } : ctx} lockContext={!replaceEv}
        replaceId={replaceEv?.id} onDone={reload} />
    </div>
  );
}

// ---------- ENGAGEMENT EVIDENCE CENTER ----------
export function EvidenceCenter({ id }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [usage, setUsage] = useState(null);
  const [filters, setFilters] = useState({});
  const [dialog, setDialog] = useState(false);

  const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v && v !== "ALL")).toString();
  const refreshId = useRef(0);
  const reload = useCallback(async () => {
    const sequence = ++refreshId.current;
    setRows([]); setUsage(null);
    try {
      const [files, storage] = await Promise.all([api.get(`/engagements/${id}/evidence?${qs}`), api.get(`/engagements/${id}/evidence/storage-usage`)]);
      if (sequence === refreshId.current) { setRows(files.data); setUsage(storage.data); }
    } catch (e) {
      if (sequence === refreshId.current) { setRows([]); setUsage(null); toast.error(getErrorMessage(e)); }
    }
  }, [id, qs]);
  useEffect(() => { loadMeta().then(setMeta); }, []);
  useEffect(() => { reload(); return () => { refreshId.current++; }; }, [reload]);

  return (
    <div className="space-y-3" data-testid="evidence-center">
      <DocumentExtractions key={id} eid={id} onSourceUnavailable={reload}/>
      {usage && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="bg-white border border-border rounded-sm p-2"><div className="text-base font-head font-bold">{humanSize(usage.total_bytes)}</div><div className="text-[10px] uppercase text-muted-foreground">Total Penyimpanan</div></div>
          <div className="bg-white border border-border rounded-sm p-2"><div className="text-base font-head font-bold">{usage.count}</div><div className="text-[10px] uppercase text-muted-foreground">Total Berkas</div></div>
          <div className="bg-white border border-border rounded-sm p-2 col-span-2"><div className="text-[10px] uppercase text-muted-foreground mb-0.5">Per Tipe</div><div className="text-[11px]">{usage.by_type.map((t) => `${t.type}: ${humanSize(t.bytes)}`).join(" · ")}</div></div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        {meta && (
          <>
            <Select value={filters.phase || "ALL"} onValueChange={(v) => setFilters({ ...filters, phase: v })}>
              <SelectTrigger className="h-8 text-xs w-52" data-testid="ec-filter-phase"><SelectValue placeholder="Semua Fase" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Semua Fase</SelectItem>{meta.phases.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.status || "ALL"} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="h-8 text-xs w-44"><SelectValue placeholder="Semua Status" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Semua Status</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        <Input className="h-8 text-xs w-40" placeholder="Filter Ref KKA" value={filters.wp_ref || ""} onChange={(e) => setFilters({ ...filters, wp_ref: e.target.value })} />
        <Button size="sm" variant="outline" onClick={() => downloadFile(`/engagements/${id}/evidence/index/export`, "Indeks_Bukti.xlsx")} data-testid="ec-export"><Download size={13} className="mr-1" />Ekspor Indeks Bukti</Button>
        <Button size="sm" onClick={() => setDialog(true)} data-testid="ec-add"><Upload size={13} className="mr-1" />Tambah Bukti (Fase Apa Pun)</Button>
      </div>
      <div className="bg-white border border-border rounded-sm p-2">
        <EvidenceTable eid={id} rows={rows} reload={reload} onReplace={() => setDialog(true)} />
      </div>
      <UploadDialog open={dialog} onOpenChange={setDialog} eid={id} defaults={{ phase: filters.phase !== "ALL" ? filters.phase : "" }} lockContext={false} onDone={reload} />
    </div>
  );
}
